import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import emailjs from "@emailjs/browser";
import {
  Phone,
  Upload,
  ArrowRight,
  CheckCircle,
  Zap,
  Shield,
  RefreshCw,
  Wrench,
  Flame,
  Gauge,
  HardHat,
  Settings,
  Cog,
  Twitter,
  Linkedin,
  Facebook,
} from "lucide-react";

// EmailJS config (set values in .env)
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_WORKER_TEMPLATE =
  process.env.REACT_APP_EMAILJS_WORKER_TEMPLATE_ID;
const EMAILJS_CLIENT_TEMPLATE =
  process.env.REACT_APP_EMAILJS_CLIENT_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// UTM Parameter Capture
function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_content: params.get("utm_content") || "",
    utm_term: params.get("utm_term") || "",
  };
}

// Trade data
const trades = [
  {
    name: "Pipefitters",
    description: "Pipe installation, maintenance, and repair",
    certification: "NSTC card required",
    icon: Wrench,
  },
  {
    name: "Welders",
    description: "Structural and pipeline welding",
    certification: "NSTC card + CWB/AWS cert",
    icon: Flame,
  },
  {
    name: "Heavy Equipment Operators",
    description: "Excavators, dozers, cranes",
    certification: "NSTC card + equipment tickets",
    icon: Gauge,
  },
  {
    name: "Instrumentation Techs",
    description: "Instrumentation and controls",
    certification: "NSTC card required",
    icon: Settings,
  },
  {
    name: "Roustabouts",
    description: "General rig labor and support",
    certification: "NSTC card required",
    icon: HardHat,
  },
  {
    name: "Heavy Duty Mechanics",
    description: "Diesel and heavy equipment repair",
    certification: "NSTC card + trade ticket",
    icon: Cog,
  },
];

// Blog placeholder data
const blogPosts = [
  {
    tag: "Project Update",
    title:
      "Willow Project: What the 2025 Construction Phase Means for Labor Demand",
    excerpt:
      "ConocoPhillips' Willow Project is entering its most labor-intensive phase. Here's what tradesmen need to know.",
  },
  {
    tag: "Certification Guide",
    title: "NSTC Certification: The Complete Guide for Lower 48 Workers",
    excerpt:
      "Everything you need to know about getting your North Slope Training Cooperative card before your first rotation.",
  },
  {
    tag: "Industry",
    title:
      "FIFO Rotations Explained: What to Expect on Your First North Slope Assignment",
    excerpt:
      "Fly-in fly-out work is different from anything else in the trades. Here's an honest look at what the schedule actually looks like.",
  },
];

// Header Component
const Header = ({ scrollToAbout }) => (
  <header className="header" data-testid="header">
    <a href="#about-section" onClick={(e) => { if (scrollToAbout) { e.preventDefault(); scrollToAbout(); } }} className="logo" data-testid="logo" aria-label="North Slope Trades — scroll to About">
      <img src="/logos/logo-facebook.svg" alt="North Slope Trades logo" className="logo-image" height={40} width={40} />
      <span className="logo-text">North <span>Slope</span> Trades</span>
    </a>
    <a href="tel:+16472500478" className="header-phone" data-testid="header-phone" aria-label="Call North Slope Trades">
      <Phone size={18} aria-hidden="true" />
      <span>+1 (647) 250-0478</span>
    </a>
  </header>
);

// Hero Section
const HeroSection = ({ scrollToWorkerForm, scrollToClientForm, scrollToAbout }) => (
  <section className="hero-section" data-testid="hero-section">
    <Header scrollToAbout={scrollToAbout} />
    <div className="hero-content">
      <h1 className="hero-headline" data-testid="hero-title">
        <span className="hero-headline-primary">North Slope Ready.</span>
        <span className="hero-headline-accent">Deployed in 72 Hours.</span>
      </h1>
      <p className="hero-subheadline" data-testid="hero-subtitle">
        Pre-vetted, FIFO-ready tradesmen for Alaska's most demanding energy
        operations.
      </p>
      <div className="hero-buttons">
        <button
          onClick={scrollToWorkerForm}
          className="btn-primary"
          data-testid="hero-join-roster-btn"
        >
          Join the Deployment Roster
        </button>
        <button
          onClick={scrollToClientForm}
          className="btn-outline"
          data-testid="hero-request-workers-btn"
        >
          Request Workers
        </button>
      </div>
      <div className="trust-bar" data-testid="trust-bar">
        <p>
          <span className="trust-bar-accent">—</span>
          Deploying certified tradesmen to Prudhoe Bay, Deadhorse, and the
          Willow Project corridor
        </p>
      </div>
      <LogoMarquee />
    </div>
  </section>
);

// Client Logo Marquee
const clientLogos = [
  { name: 'PCL Construction', src: '/logos/pcl-construction.svg' },
  { name: 'Hilcorp', src: '/logos/hilcorp.svg' },
  { name: 'ConocoPhillips', src: '/logos/conocophillips.svg' },
  { name: 'TechnipFMC', src: '/logos/technipfmc.svg' },
];

const LogoMarquee = () => (
  <div className="logo-marquee-section" data-testid="logo-marquee">
    <p className="logo-marquee-subtitle">Trusted on the Toughest Sites</p>
    <div className="logo-marquee-container">
      <div className="logo-marquee-track">
        {[...clientLogos, ...clientLogos, ...clientLogos, ...clientLogos].map((logo, index) => (
          <div key={index} className="logo-marquee-item">
            <img 
              src={logo.src} 
              alt={logo.name} 
              className="logo-marquee-image"
            />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// About Us Section
const AboutSection = () => (
  <section className="about-section section-dark-2" id="about-section" data-testid="about-section">
    <div className="about-container section">
      <div className="about-grid">
        {/* Left Column - Image */}
        <div className="about-image-column">
          <div className="about-image-wrapper">
            <img 
              src="/logos/ansh-headshot.jpg" 
              alt="Ansh Mukherjee - CEO" 
              className="about-image"
            />
          </div>
        </div>
        
        {/* Right Column - Text & Contact */}
        <div className="about-text-column">
          <span className="about-tag">Leadership</span>
          <h2 className="about-name">Ansh Mukherjee</h2>
          <p className="about-title">Co-founder | CEO</p>
          
          <p className="about-bio">
            Ansh is a veteran of enterprise logistics and B2B growth across North America, EMEA, and the Middle East. With over 14 years of experience driving complex, multi-million-dollar deployments, Ansh cut his teeth managing global subcontractor networks for offshore heavyweights like TechnipFMC and driving natural gas sector expansions at Clean Energy Fuels.
          </p>
          
          <p className="about-bio">
            Today, he bridges the gap between high-level enterprise management and on-the-ground talent logistics. Having scaled operations and managed multi-million dollar portfolios across international territories, Ansh understands exactly what Field Superintendents and Project Directors need to keep multi-billion-dollar infrastructure phases on schedule: precision, compliance, and rapid mobilization.
          </p>
          
          <div className="about-contact">
            <a href="mailto:hello@northslopetrades.com" className="about-contact-item">
              <svg className="about-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              hello@northslopetrades.com
            </a>
            <a href="tel:+16472500478" className="about-contact-item">
              <Phone size={16} className="about-contact-icon" />
              +1 (647) 250-0478
            </a>
          </div>
          
          <a 
            href="https://www.linkedin.com/company/north-slope-trades/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="about-linkedin-btn"
            data-testid="about-linkedin-btn"
          >
            <Linkedin size={18} />
            Connect on LinkedIn
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Stats Bar - Replaces Map Section
const StatsBar = () => (
  <section className="stats-bar" data-testid="stats-bar">
    <div className="stats-container">
      <div className="stats-grid">
        <div className="stat-item" data-testid="stat-deployment">
          <span className="stat-number">72</span>
          <span className="stat-label">Hour Deployment</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item" data-testid="stat-verified">
          <span className="stat-number">100%</span>
          <span className="stat-label">NSTC Verified</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item" data-testid="stat-fifo">
          <span className="stat-number">FIFO</span>
          <span className="stat-label">Ready Candidates</span>
        </div>
      </div>
    </div>
  </section>
);

// Specializations Section
const SpecializationsSection = () => (
  <section
    className="section-dark-3 section-full"
    data-testid="specializations-section"
  >
    <div className="section">
      <h2 className="section-title" data-testid="specializations-title">
        The North Slope Workforce,{" "}
        <span className="accent-blue">Ready to Deploy</span>
      </h2>
      <p className="section-subtitle" data-testid="specializations-subtitle">
        Certified tradesmen across all critical oil & gas disciplines.
        NSTC-verified and rotation-ready.
      </p>
      <div className="trade-grid" data-testid="trade-grid">
        {trades.map((trade, index) => {
          const IconComponent = trade.icon;
          return (
            <div
              key={index}
              className="trade-card"
              data-testid={`trade-card-${index}`}
            >
              <IconComponent className="trade-icon" />
              <h3 className="trade-name">{trade.name}</h3>
              <p className="trade-desc">{trade.description}</p>
              <span className="trade-cert">{trade.certification}</span>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// How It Works Section
const HowItWorksSection = () => (
  <section
    className="hiw-section section-full section-dark-2"
    data-testid="how-it-works-section"
  >
    <div className="hiw-bg" />
    <div className="hiw-content section">
      <h2 className="section-title" data-testid="hiw-title">
        How the Deployment Roster <span className="accent-blue">Works</span>
      </h2>
      <div className="hiw-steps">
        <div className="hiw-step" data-testid="hiw-step-1">
          <div className="hiw-step-number">1</div>
          <h3 className="hiw-step-title">Submit Your Profile</h3>
          <p className="hiw-step-desc">
            Fill out the form below with your trade, certifications, and
            availability
          </p>
        </div>
        <div className="hiw-step" data-testid="hiw-step-2">
          <div className="hiw-step-number">2</div>
          <h3 className="hiw-step-title">We Verify Your Tickets</h3>
          <p className="hiw-step-desc">
            Our team confirms your NSTC card and deployment readiness
          </p>
        </div>
        <div className="hiw-step" data-testid="hiw-step-3">
          <div className="hiw-step-number">3</div>
          <h3 className="hiw-step-title">Get Matched to a Rotation</h3>
          <p className="hiw-step-desc">
            When an operator needs your trade, we reach out with a real
            opportunity
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Worker Form Section
const WorkerFormSection = ({ formRef }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    primary_trade: "",
    years_experience: "",
    nstc_card: "",
    anchorage_travel: "",
    availability: "",
    resume_link: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showKnockout, setShowKnockout] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Check knockout conditions
    if (name === "nstc_card" || name === "anchorage_travel") {
      const newData = { ...formData, [name]: value };
      if (newData.nstc_card === "no" && newData.anchorage_travel === "no") {
        setShowKnockout(true);
      } else {
        setShowKnockout(false);
      }
    }
  };

  const uploadToCloudinary = async (file) => {
    const url = "https://api.cloudinary.com/v1_1/dp8xk8fam/raw/upload";

    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "resume_upload");

    const response = await fetch(url, { method: "POST", body: data });
    if (!response.ok) {
      const errBody = await response.text();
      console.error("Cloudinary error:", response.status, errBody);
      throw new Error("Upload failed");
    }

    const result = await response.json();
    console.log("Resume URL", result);

    return result.secure_url;
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFileName(file.name);
      setIsUploading(true);

      try {
        const resumeUrl = await uploadToCloudinary(file);
        setFormData((prev) => ({ ...prev, resume_link: resumeUrl }));
      } catch (error) {
        console.error("Resume upload error:", error);
        setFileName("");
        setFormData((prev) => ({ ...prev, resume_link: "" }));
        alert("Failed to upload resume. Please try again.");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Knockout check
    if (formData.nstc_card === "no" && formData.anchorage_travel === "no") {
      setShowKnockout(true);
      // Fire GA4 knockout event
      if (typeof window.gtag === "function") {
        window.gtag("event", "worker_knockout", {
          event_category: "Lead Capture",
          event_label: "Disqualified — Missing NSTC or Travel",
        });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Send EmailJS notification
      await emailjs.send(
        "service_eghnbyh",
        "template_gbmyxuq",
        {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          primary_trade: formData.primary_trade,
          years_experience: formData.years_experience,
          nstc_card: formData.nstc_card,
          anchorage_travel: formData.anchorage_travel,
          availability: formData.availability,
          resume_link: formData.resume_link
            ? formData.resume_link.replace("/image/upload/", "/raw/upload/")
            : "Not provided",
        },
        "2LQupVOaO-zlqxqH2",
      );

      // Fire GA4 success event
      if (typeof window.gtag === "function") {
        window.gtag("event", "worker_form_submit", {
          event_category: "Lead Capture",
          event_label: "Worker Intake Form",
        });
      }
      // Fire Facebook Pixel event
      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Worker Intake Form" });
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Form submission error:", error);
      alert("There was an error submitting your form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section
        ref={formRef}
        className="form-section section"
        id="worker-form-section"
        data-testid="worker-form-section"
      >
        <div className="form-success" data-testid="worker-form-success">
          <CheckCircle className="form-success-icon" />
          <h3 className="form-success-title">You're on the list</h3>
          <p className="form-success-text">
            We'll be in touch when a North Slope match opens up. In the
            meantime, make sure your NSTC card is current.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={formRef}
      className="form-section section section-dark-2"
      id="worker-form-section"
      data-testid="worker-form-section"
    >
      <h2 className="section-title" data-testid="worker-form-title">
        Get on the North Slope{" "}
        <span className="accent-orange">Deployment List</span>
      </h2>
      <p className="section-subtitle" data-testid="worker-form-subtitle">
        High-paying FIFO rotations for certified tradesmen. Submit your profile
        and we'll reach out when a match opens.
      </p>

      <form
        id="worker-form"
        className="form-container"
        onSubmit={handleSubmit}
        data-testid="worker-form"
      >
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">
              Full Name <span>*</span>
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              className="form-input"
              required
              data-testid="worker-full-name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Email <span>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="form-input"
              required
              data-testid="worker-email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Phone <span>*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="form-input"
              required
              data-testid="worker-phone"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Primary Trade <span>*</span>
            </label>
            <select
              name="primary_trade"
              value={formData.primary_trade}
              onChange={handleInputChange}
              className="form-select"
              required
              data-testid="worker-trade"
            >
              <option value="">Select your trade</option>
              <option value="pipefitter">Pipefitter</option>
              <option value="welder">Welder</option>
              <option value="instrumentation_tech">Instrumentation Tech</option>
              <option value="heavy_equipment_operator">
                Heavy Equipment Operator
              </option>
              <option value="heavy_duty_mechanic">Heavy Duty Mechanic</option>
              <option value="roustabout">Roustabout</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Years of Experience <span>*</span>
            </label>
            <select
              name="years_experience"
              value={formData.years_experience}
              onChange={handleInputChange}
              className="form-select"
              required
              data-testid="worker-experience"
            >
              <option value="">Select experience</option>
              <option value="1-3">1–3 years</option>
              <option value="3-5">3–5 years</option>
              <option value="5-10">5–10 years</option>
              <option value="10+">10+ years</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Availability <span>*</span>
            </label>
            <select
              name="availability"
              value={formData.availability}
              onChange={handleInputChange}
              className="form-select"
              required
              data-testid="worker-availability"
            >
              <option value="">Select availability</option>
              <option value="immediately">Immediately</option>
              <option value="2_weeks">Within 2 weeks</option>
              <option value="1_month">Within 1 month</option>
              <option value="1_month_plus">1 month+</option>
            </select>
          </div>

          <div className="form-group full-width">
            <label className="form-label">
              Do you hold a current, valid NSTC card? <span>*</span>
            </label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="nstc_card"
                  value="yes"
                  checked={formData.nstc_card === "yes"}
                  onChange={handleInputChange}
                  className="radio-input"
                  required
                  data-testid="worker-nstc-yes"
                />
                Yes
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="nstc_card"
                  value="no"
                  checked={formData.nstc_card === "no"}
                  onChange={handleInputChange}
                  className="radio-input"
                  data-testid="worker-nstc-no"
                />
                No
              </label>
            </div>
          </div>

          <div className="form-group full-width">
            <label className="form-label">
              Are you willing to fund your own travel to Anchorage as Point of
              Hire? <span>*</span>
            </label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="anchorage_travel"
                  value="yes"
                  checked={formData.anchorage_travel === "yes"}
                  onChange={handleInputChange}
                  className="radio-input"
                  required
                  data-testid="worker-travel-yes"
                />
                Yes
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="anchorage_travel"
                  value="no"
                  checked={formData.anchorage_travel === "no"}
                  onChange={handleInputChange}
                  className="radio-input"
                  data-testid="worker-travel-no"
                />
                No
              </label>
            </div>
          </div>

          <div className="form-group full-width">
            <label className="form-label">
              Resume / Certifications (optional)
            </label>
            <div className="file-upload">
              <input
                type="file"
                onChange={handleFileChange}
                className="file-upload-input"
                accept=".pdf,.doc,.docx"
                data-testid="worker-resume"
              />
              <div className="file-upload-label">
                {isUploading ? <span className="spinner" /> : <Upload size={20} />}
                {isUploading ? "Uploading..." : fileName || "Upload Resume & Tickets"}
              </div>
            </div>
          </div>
        </div>

        {showKnockout && (
          <div className="knockout-message" data-testid="knockout-message">
            <p>
              These roles require a valid NSTC card and travel to Anchorage as
              Point of Hire. We'd encourage you to get certified and apply again
              — we'd love to have you on the roster.
            </p>
          </div>
        )}

        <div className="form-submit">
          <button
            type="submit"
            id="worker-submit"
            className="btn-primary w-full"
            disabled={isSubmitting || showKnockout || isUploading}
            data-testid="worker-submit-btn"
          >
            {isSubmitting ? <span className="spinner" /> : "Submit My Profile"}
          </button>
          <p className="form-privacy" data-testid="worker-form-privacy">
            Your information is never shared without your consent. No spam
            calls.
          </p>
          <p className="form-consent" data-testid="worker-form-consent">
            By submitting this form, you agree to receive calls and text messages from North Slope Trades regarding recruitment opportunities, account updates, and services. Message frequency varies. Message and data rates may apply. You can reply STOP to opt-out at any time.
          </p>
        </div>
      </form>
    </section>
  );
};

// Client Form Section
const ClientFormSection = ({ formRef }) => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    role_title: "",
    trades_needed: [],
    workers_required: "",
    project_location: "",
    start_date: "",
    email: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTradeChange = (trade) => {
    setFormData((prev) => {
      const trades = prev.trades_needed.includes(trade)
        ? prev.trades_needed.filter((t) => t !== trade)
        : [...prev.trades_needed, trade];
      return { ...prev, trades_needed: trades };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send EmailJS notification
      await emailjs.send(
        "service_eghnbyh",
        "template_xnkudl6",
        {
          company_name: formData.company_name,
          contact_name: formData.contact_name,
          role_title: formData.role_title,
          trades_needed: formData.trades_needed.join(", ") || "Not specified",
          workers_required: formData.workers_required || "Not specified",
          project_location: formData.project_location || "Not specified",
          start_date: formData.start_date || "Not specified",
          email: formData.email,
          phone: formData.phone,
        },
        "2LQupVOaO-zlqxqH2",
      );

      // Fire GA4 success event
      if (typeof window.gtag === "function") {
        window.gtag("event", "client_form_submit", {
          event_category: "Lead Capture",
          event_label: "Client Request Form",
        });
      }
      // Fire Facebook Pixel event
      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Client Request Form" });
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Form submission error:", error);
      alert("There was an error submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const tradeOptions = [
    "Pipefitter",
    "Welder",
    "Instrumentation Tech",
    "Heavy Equipment Operator",
    "Heavy Duty Mechanic",
    "Roustabout",
  ];

  if (isSubmitted) {
    return (
      <section
        ref={formRef}
        className="client-section section"
        id="client-form-section"
        data-testid="client-form-section"
      >
        <div className="form-success" data-testid="client-form-success">
          <CheckCircle className="form-success-icon" />
          <h3 className="form-success-title">Request Received</h3>
          <p className="form-success-text">
            We'll review your requirements and be in touch within 24 hours.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={formRef}
      className="client-section section section-dark-3"
      id="client-form-section"
      data-testid="client-form-section"
    >
      <h2 className="section-title" data-testid="client-form-title">
        Need Certified Crew on the Slope —{" "}
        <span className="accent-blue">Fast?</span>
      </h2>
      <p className="section-subtitle" data-testid="client-form-subtitle">
        We maintain a pre-screened bench of NSTC-certified, FIFO-ready
        tradesmen. No HR bottlenecks. No wasted interviews. Just
        deployment-ready people.
      </p>
      <p className="bench-signal" data-testid="bench-signal">
        <CheckCircle size={16} /> Bench currently active — last worker
        placement: <span>recently</span>
      </p>

      <div className="client-values" data-testid="client-values">
        <div className="client-value">
          <Zap className="client-value-icon" />
          <div>
            <h4 className="client-value-title">24–72 Hour Mobilization</h4>
            <p className="client-value-desc">
              Deployment-ready candidates, not database entries
            </p>
          </div>
        </div>
        <div className="client-value">
          <Shield className="client-value-icon" />
          <div>
            <h4 className="client-value-title">Pre-Screened & Certified</h4>
            <p className="client-value-desc">
              NSTC verified before we ever pitch a name
            </p>
          </div>
        </div>
        <div className="client-value">
          <RefreshCw className="client-value-icon" />
          <div>
            <h4 className="client-value-title">FIFO-Ready</h4>
            <p className="client-value-desc">
              Candidates familiar with rotational schedules and remote sites
            </p>
          </div>
        </div>
      </div>

      <form
        id="client-form"
        className="form-container"
        onSubmit={handleSubmit}
        data-testid="client-form"
      >
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">
              Company Name <span>*</span>
            </label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              className="form-input"
              required
              data-testid="client-company"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Your Name <span>*</span>
            </label>
            <input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleInputChange}
              className="form-input"
              required
              data-testid="client-name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Your Role / Title <span>*</span>
            </label>
            <input
              type="text"
              name="role_title"
              value={formData.role_title}
              onChange={handleInputChange}
              className="form-input"
              required
              data-testid="client-role"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Number of Workers Required</label>
            <input
              type="number"
              name="workers_required"
              value={formData.workers_required}
              onChange={handleInputChange}
              className="form-input"
              min="1"
              data-testid="client-workers"
            />
          </div>

          <div className="form-group full-width">
            <label className="form-label">
              Trade(s) Needed <span>*</span>
            </label>
            <div className="checkbox-group">
              {tradeOptions.map((trade, index) => (
                <label key={index} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.trades_needed.includes(trade)}
                    onChange={() => handleTradeChange(trade)}
                    className="checkbox-input"
                    data-testid={`client-trade-${index}`}
                  />
                  {trade}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Project / Site Location</label>
            <input
              type="text"
              name="project_location"
              value={formData.project_location}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g. Prudhoe Bay, Deadhorse, Willow"
              data-testid="client-location"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Estimated Start Date</label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              className="form-input"
              data-testid="client-start-date"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Email <span>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="form-input"
              required
              data-testid="client-email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Phone <span>*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="form-input"
              required
              data-testid="client-phone"
            />
          </div>
        </div>

        <div className="form-submit">
          <button
            type="submit"
            id="client-submit"
            className="btn-outline w-full"
            disabled={isSubmitting}
            data-testid="client-submit-btn"
          >
            {isSubmitting ? (
              <span className="spinner" />
            ) : (
              "Submit a Crew Request"
            )}
          </button>
          <p className="form-consent" data-testid="client-form-consent">
            By submitting this form, you agree to receive calls and text messages from North Slope Trades regarding recruitment opportunities, account updates, and services. Message frequency varies. Message and data rates may apply. You can reply STOP to opt-out at any time.
          </p>
        </div>
      </form>
    </section>
  );
};

// Social Tag for Footer
const SocialTag = () => (
  <div className="social-tag" data-testid="social-tag">
    <span className="social-tag-label">North Slope Intelligence</span>
    <div className="social-tag-icons">
      <a 
        href="https://x.com/northslopejobs?s=21" 
        target="_blank" 
        rel="noopener noreferrer"
        className="social-tag-link"
        aria-label="Follow on X/Twitter"
        data-testid="social-twitter-link"
      >
        <Twitter size={16} />
      </a>
      <a 
        href="https://www.linkedin.com/company/north-slope-trades/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="social-tag-link"
        aria-label="Follow on LinkedIn"
        data-testid="social-linkedin-link"
      >
        <Linkedin size={16} />
      </a>
      <a 
        href="https://www.facebook.com/share/1ar6CxbWJz/?mibextid=wwXIfr" 
        target="_blank" 
        rel="noopener noreferrer"
        className="social-tag-link"
        aria-label="Follow on Facebook"
        data-testid="social-facebook-link"
      >
        <Facebook size={16} />
      </a>
    </div>
  </div>
);

// FAQ Section
const faqs = [
  {
    q: "How fast can North Slope Trades deploy tradesmen to Alaska?",
    a: "North Slope Trades deploys pre-vetted, NSTC-certified tradesmen to Alaska's North Slope within 72 hours of a confirmed request. Our roster is kept rotation-ready so operators at Prudhoe Bay, Deadhorse, and the Willow Project corridor can mobilize crews without waiting on sourcing or onboarding."
  },
  {
    q: "What trades do you supply for North Slope oil & gas operations?",
    a: "We supply pipefitters, welders (CWB/AWS certified), heavy equipment operators, instrumentation technicians, roustabouts, and heavy-duty mechanics. Every candidate holds a current NSTC (North Slope Training Cooperative) card and is FIFO-ready for rotational schedules."
  },
  {
    q: "What is an NSTC card and why is it required?",
    a: "The NSTC (North Slope Training Cooperative) card is the mandatory safety certification required by all major operators — including ConocoPhillips, Hilcorp, and their tier-one contractors — for anyone working on Alaska's North Slope. Without a current NSTC card, a tradesman cannot step onto a North Slope job site."
  },
  {
    q: "Do you cover travel to Anchorage or to the North Slope?",
    a: "Yes. Standard FIFO rotations for our client operators include flights from Anchorage to Deadhorse, lodging at camp, and meals. Candidates must be able to travel to Anchorage on their own for the staging flight."
  },
  {
    q: "Which companies does North Slope Trades work with?",
    a: "North Slope Trades supplies labor into projects operated by or contracting under ConocoPhillips, Hilcorp, PCL Construction, TechnipFMC, and other tier-one North Slope operators. Specific deployments include ongoing work around Prudhoe Bay, Deadhorse, and the ConocoPhillips Willow Project."
  },
  {
    q: "How do tradesmen apply to join the deployment roster?",
    a: "Tradesmen can submit the Deployment Roster form on this page with their trade, years of experience, NSTC status, and availability. Our recruiters verify certifications and reach out the moment a matching rotation opens."
  },
  {
    q: "Where is North Slope Trades based?",
    a: "North Slope Trades operates out of Alaska and serves the North Slope and Western Canadian energy sectors. Deployments are coordinated from Anchorage with labor dispatched to Prudhoe Bay, Deadhorse, and surrounding operator-controlled camps."
  }
];

const FAQSection = () => (
  <section className="faq-section section section-dark-2" id="faq-section" data-testid="faq-section">
    <h2 className="section-title" data-testid="faq-title">
      Frequently Asked <span className="accent-blue">Questions</span>
    </h2>
    <p className="section-subtitle" data-testid="faq-subtitle">
      What tradesmen and operators ask us most about deploying labor to Alaska's North Slope.
    </p>
    <div className="faq-list" data-testid="faq-list">
      {faqs.map((faq, i) => (
        <details key={i} className="faq-item" data-testid={`faq-item-${i}`}>
          <summary className="faq-question">
            <span>{faq.q}</span>
            <span className="faq-icon" aria-hidden="true">+</span>
          </summary>
          <p className="faq-answer">{faq.a}</p>
        </details>
      ))}
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="footer-content">
      <div className="footer-top">
        <div className="footer-brand">
          <div className="footer-logo">
            North <span>Slope</span> Trades
          </div>
          <p className="footer-tagline">
            Rapid labor deployment for Alaska's North Slope & Western Canadian Energy Sector.
          </p>
        </div>
        <div className="footer-contact">
          <a href="tel:+16472500478" className="footer-phone" data-testid="footer-phone">
            +1 (647) 250-0478
          </a>
          <a href="mailto:hello@northslopetrades.com" className="footer-email" data-testid="footer-email">
            hello@northslopetrades.com
          </a>
          <div className="footer-links">
            <a href="#" className="footer-link" data-testid="footer-privacy">
              Privacy Policy
            </a>
            <a href="#" className="footer-link" data-testid="footer-terms">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p className="footer-copyright" data-testid="footer-copyright">
          © 2025 North Slope Trades. All rights reserved.
        </p>
        <SocialTag />
      </div>
    </div>
  </footer>
);

// Mobile CTA Component
const MobileCTA = ({ scrollToWorkerForm, scrollToClientForm, isHidden }) => (
  <div
    className={`mobile-cta ${isHidden ? "hidden" : ""}`}
    data-testid="mobile-cta"
  >
    <button
      onClick={scrollToWorkerForm}
      className="btn-primary"
      data-testid="mobile-join-btn"
    >
      Join Roster
    </button>
    <button
      onClick={scrollToClientForm}
      className="btn-outline"
      data-testid="mobile-request-btn"
    >
      Request Workers
    </button>
  </div>
);

// Main App Component
function App() {
  const workerFormRef = useRef(null);
  const clientFormRef = useRef(null);
  const [hideMobileCTA, setHideMobileCTA] = useState(false);

  const scrollToWorkerForm = () => {
    workerFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToClientForm = () => {
    clientFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToAbout = () => {
    const el = document.getElementById('about-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!workerFormRef.current || !clientFormRef.current) return;

      const workerRect = workerFormRef.current.getBoundingClientRect();
      const clientRect = clientFormRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const workerInView =
        workerRect.top < windowHeight && workerRect.bottom > 0;
      const clientInView =
        clientRect.top < windowHeight && clientRect.bottom > 0;

      setHideMobileCTA(workerInView || clientInView);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App" data-testid="app">
      <HeroSection 
        scrollToWorkerForm={scrollToWorkerForm} 
        scrollToClientForm={scrollToClientForm}
        scrollToAbout={scrollToAbout}
      />
      <StatsBar />
      <SpecializationsSection />
      <HowItWorksSection />
      <AboutSection />
      <WorkerFormSection formRef={workerFormRef} />
      <ClientFormSection formRef={clientFormRef} />
      <FAQSection />
      <Footer />
    
      <MobileCTA
        scrollToWorkerForm={scrollToWorkerForm}
        scrollToClientForm={scrollToClientForm}
        isHidden={hideMobileCTA}
      />
    </div>
  );
}

export default App;
